import type { ProductKind } from '@iheartjane/types';
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
/* eslint-disable-next-line no-restricted-imports */
import * as t from 'io-ts';
import chunk from 'lodash-es/chunk';
import { useMemo } from 'react';

import {
  tProductKind,
  zProductDetailResponse,
  zProductIndexResponse,
} from '@jane/business-admin/types';
import type {
  AbbreviatedMenuRow,
  GlobalSpecialProduct,
  MenuProductForProductsTable,
  ProductIndexResponse,
} from '@jane/business-admin/types';
import { fetchWithDecode } from '@jane/business-admin/util';
import { janeApiV2, safeParseAsync } from '@jane/shared/data-access';
import type { Photo } from '@jane/shared/types';

import { STORES_URL } from './stores';

export const invalidateAllProductQueries = (
  queryClient: QueryClient,
  storeId: string | number
) => {
  queryClient.invalidateQueries(['searchStoreProducts', storeId]);
  queryClient.invalidateQueries(['storeProducts', storeId]);
  queryClient.invalidateQueries(['storeUnpublishedProducts', storeId]);
};

const fetchMenuRows = async (
  storeId: string,
  customDisplayName?: string
): Promise<AbbreviatedMenuRow[]> => {
  const urlParams = new URLSearchParams();
  customDisplayName &&
    urlParams.append('custom_display_name', customDisplayName);

  const url = `${STORES_URL}/${storeId}/menu_rows?${urlParams.toString()}`;
  const tPayload = t.type({
    custom_rows: t.any,
  });

  const data = await fetchWithDecode(
    janeApiV2.get<{ custom_rows: AbbreviatedMenuRow[] }>(url),
    tPayload,
    url
  );
  return data.custom_rows;
};
export const useFetchMenuRows = (
  storeId: string,
  customDisplayName?: string
) => {
  return useQuery<AbbreviatedMenuRow[]>({
    queryFn: () => fetchMenuRows(storeId, customDisplayName),
    queryKey: ['stores', storeId, customDisplayName],
    useErrorBoundary: true,
  });
};

const fetchProductDetails = async (
  storeId: string,
  menuProductId?: string,
  productId?: string
) => {
  const pathEnd = menuProductId ? menuProductId : 'show';
  const queryParams = productId ? `?product_id=${productId}` : '';
  const url = `${STORES_URL}/${storeId}/products/${pathEnd}${queryParams}`;
  const response = await janeApiV2.get<{
    product: MenuProductForProductsTable;
  }>(url);

  return await safeParseAsync(
    zProductDetailResponse,
    response,
    'fetchProductDetails'
  );
};

export const useFetchProductDetails = (
  storeId: string,
  { menuProductId, productId }: { menuProductId?: string; productId?: string }
) => {
  return useQuery({
    queryKey: ['storeProducts', storeId, menuProductId, productId],
    queryFn: () => fetchProductDetails(storeId, menuProductId, productId),
  });
};

/**
 * Used to fetch details for multiple product_ids at once.
 * @param storeId Store id
 * @param product_ids List of product_ids, one fetch will happen per product_id
 * @returns List of queries with data containing the product details
 */
export const useFetchMultipleProductDetails = (
  storeId: string,
  ids: number[],
  useMenuProductId = false,
  enabled = true
) => {
  return useQueries({
    queries: !enabled
      ? []
      : ids.map((id) => {
          return {
            queryKey: ['storeProducts', storeId, id, useMenuProductId],
            queryFn: () =>
              fetchProductDetails(
                storeId,
                useMenuProductId ? id?.toString() : undefined,
                useMenuProductId ? undefined : id?.toString()
              ),
          };
        }),
  });
};

export type fetchProductsProps = {
  brand?: string;
  brand_subtype?: string;
  category?: string;
  content?: string;
  custom_rows?: string;
  has_hidden_weights?: string;
  ids?: string[];
  in_stock?: boolean;
  include_out_of_stock?: boolean;
  lineage?: string;
  order?: string;
  page?: number;
  per_page?: number;
  product_ids?: string[];
  query?: string;
  sort?: string;
  status?: string;
  subcategory?: string;
  weight?: string;
};

const fetchProducts = async (
  storeId: string,
  params: fetchProductsProps
): Promise<ProductIndexResponse> => {
  const urlParams = new URLSearchParams();
  params.sort && urlParams.append('sort', params.sort);
  params.order && urlParams.append('order', params.order);
  params.brand && urlParams.append('brand', params.brand);
  params.brand_subtype &&
    urlParams.append('brand_subtype', params.brand_subtype);
  params.content && urlParams.append('content', params.content);
  params.query && urlParams.append('query', params.query);
  params.custom_rows && urlParams.append('custom_rows', params.custom_rows);
  params.category && urlParams.append('category', params.category);
  params.has_hidden_weights &&
    urlParams.append('has_hidden_weights', params.has_hidden_weights);
  params.in_stock !== undefined &&
    urlParams.append('in_stock', String(params.in_stock));
  params.lineage && urlParams.append('lineage', params.lineage);
  params.status && urlParams.append('status', params.status);
  params.include_out_of_stock &&
    urlParams.append(
      'include_out_of_stock',
      params.include_out_of_stock.toString()
    );
  params.page && urlParams.append('page', params.page.toString());
  params.subcategory && urlParams.append('subcategory', params.subcategory);
  params.weight && urlParams.append('weight', params.weight);
  params.per_page && urlParams.append('per_page', params.per_page.toString());
  params.ids?.forEach((id) => urlParams.append('ids[]', id));
  params.product_ids?.forEach((id) => urlParams.append('product_ids[]', id));

  const url = `${STORES_URL}/${storeId}/products?${urlParams.toString()}`;
  const response = await janeApiV2.get<ProductIndexResponse>(url);

  return await safeParseAsync(zProductIndexResponse, response, 'fetchProducts');
};

export const fetchProductQueryKey = (
  storeId: string,
  params: fetchProductsProps
) => {
  return ['storeProducts', storeId, params];
};

const BATCH_CHUNK_SIZE = 200;

type FetchProductsByIdsProps = {
  ids: string[];
  storeId: string;
  useMenuProductId: boolean;
};

export const useFetchProductsByIds = (
  { storeId, ids, useMenuProductId }: FetchProductsByIdsProps,
  options?: { enabled?: boolean }
) => {
  const enabled = (options?.enabled ?? true) && ids.length > 0;

  const chunks = useMemo(() => {
    return chunk(
      [...ids].sort((a, b) => Number(a) - Number(b)),
      BATCH_CHUNK_SIZE
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(ids)]);

  const queries = useQueries({
    queries: enabled
      ? chunks.map((chunk) => ({
          queryKey: [
            'storeProducts',
            'batch',
            storeId,
            useMenuProductId,
            chunk,
          ],
          queryFn: () =>
            fetchProducts(storeId, {
              ...(useMenuProductId ? { ids: chunk } : { product_ids: chunk }),
              per_page: chunk.length,
              include_out_of_stock: true,
            }),
        }))
      : [],
  });

  if (!enabled || queries.length === 0) {
    return {
      data: undefined,
      isLoading: false,
      isError: false,
      failureReason: undefined,
    };
  }

  const isLoading = queries.some((q) => q.isLoading);
  const errorQuery = queries.find((q) => q.isError);
  const allSuccess = queries.every((q) => q.isSuccess);

  return {
    data: allSuccess
      ? { products: queries.flatMap((q) => q.data?.products ?? []) }
      : undefined,
    isLoading,
    isError: !!errorQuery,
    failureReason: errorQuery?.failureReason,
  };
};

export const useFetchProducts = (
  storeId: string,
  params: fetchProductsProps,
  disabled?: boolean
) => {
  return useInfiniteQuery<ProductIndexResponse>({
    queryFn: async ({ pageParam = 1 }) => {
      const data = await fetchProducts(storeId, {
        sort: params.sort,
        order: params.order,
        brand: params.brand,
        brand_subtype: params.brand_subtype,
        content: params.content,
        query: params.query,
        custom_rows: params.custom_rows,
        category: params.category,
        has_hidden_weights: params.has_hidden_weights,
        in_stock: params.in_stock,
        lineage: params.lineage,
        status: params.status,
        include_out_of_stock: params.include_out_of_stock,
        ids: params.ids,
        subcategory: params.subcategory,
        weight: params.weight,
        per_page: params.per_page,
        page: pageParam,
      });
      return {
        ...data,
        pageParam,
      };
    },
    enabled: !disabled,
    queryKey: fetchProductQueryKey(storeId, params),
    getNextPageParam: (lastPage) => {
      const hasNextPage = lastPage?.meta.page < lastPage.meta.number_of_pages;
      return hasNextPage ? lastPage.meta.page + 1 : undefined;
    },
    staleTime: Infinity,
    useErrorBoundary: true,
  });
};

const updateProductVisibility = async (
  data: { visible: boolean },
  storeId: string,
  productId: string
): Promise<null> => {
  return await janeApiV2.put<null>(
    `${STORES_URL}/${storeId}/products/${productId}`,
    {
      attributes: data,
    }
  );
};

export const useUpdateProductVisibility = (storeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      productId,
    }: {
      data: { visible: boolean };
      productId: string;
    }) => updateProductVisibility(data, storeId, productId),
    onError: (error) => {
      console.error(error);
    },
    onSuccess: () => {
      invalidateAllProductQueries(queryClient, storeId);
    },
  });
};

type updateBulkProductsProps = {
  description?: string;
  percent_cbd?: null;
  percent_cbda?: null;
  percent_thc?: null;
  percent_thca?: null;
  visible?: boolean;
};

const updateBulkProducts = async (
  data: updateBulkProductsProps,
  storeId: string,
  productIds: string[],
  deletePhotos?: boolean,
  allProducts?: boolean
): Promise<null> => {
  return await janeApiV2.put<null>(
    `${STORES_URL}/${storeId}/products/bulk_update`,
    {
      attributes: data,
      product_ids: productIds,
      delete_photos: !!deletePhotos,
      all_products: !!allProducts,
    }
  );
};

export const useUpdateBulkProducts = (storeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      allProducts,
      data,
      productIds,
      deletePhotos,
    }: {
      allProducts?: boolean;
      data: updateBulkProductsProps;
      deletePhotos?: boolean;
      productIds: string[];
    }) =>
      updateBulkProducts(data, storeId, productIds, deletePhotos, allProducts),
    onSuccess: () => {
      invalidateAllProductQueries(queryClient, storeId);
    },
  });
};

const updateBulkProductsAsync = async (
  data: updateBulkProductsProps,
  storeId: string,
  productIds: string[],
  deletePhotos?: boolean,
  allProducts?: boolean
): Promise<null> => {
  return await janeApiV2.put<null>(
    `${STORES_URL}/${storeId}/products/bulk_update_async`,
    {
      attributes: data,
      product_ids: productIds,
      delete_photos: !!deletePhotos,
      all_products: !!allProducts,
    }
  );
};

export const useUpdateBulkProductsAsync = (storeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      allProducts,
      data,
      productIds,
      deletePhotos,
    }: {
      allProducts?: boolean;
      data: updateBulkProductsProps;
      deletePhotos?: boolean;
      productIds: string[];
    }) =>
      updateBulkProductsAsync(
        data,
        storeId,
        productIds,
        deletePhotos,
        allProducts
      ),
    onSuccess: () => {
      invalidateAllProductQueries(queryClient, storeId);
    },
  });
};

interface AutoPublishSettingsPayload {
  product_types: ProductKind[];
}

const fetchAutoPublishSettings = async (
  storeId: string
): Promise<ProductKind[]> => {
  const url = `${STORES_URL}/${storeId}/products/auto_publish`;

  const data = await fetchWithDecode(
    janeApiV2.get<AutoPublishSettingsPayload>(url),
    t.type({ product_types: t.array(tProductKind) }),
    url
  );

  return data.product_types;
};

export const useAutoPublishSettings = (storeId: string) =>
  useQuery<ProductKind[]>({
    queryFn: () => fetchAutoPublishSettings(storeId),
    queryKey: ['stores', storeId, 'auto_publish'],
    useErrorBoundary: true,
    staleTime: Infinity,
  });

const updateAutoPublishSettings = async (
  storeId: string,
  data: AutoPublishSettingsPayload
): Promise<null> =>
  janeApiV2.put<null>(`${STORES_URL}/${storeId}/products/auto_publish`, data);

export const useUpdateAutoPublishSettings = (storeId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AutoPublishSettingsPayload) =>
      updateAutoPublishSettings(storeId, data),
    onError: (error) => {
      console.error(error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['stores', storeId, 'auto_publish']);
    },
  });
};

export interface UnavailablePrice {
  _destroy?: boolean;
  id?: number;
  price_id: string;
}

export interface FlavorEffectKinds {
  _destroy?: boolean;
  id?: number;
  kind: string;
}

export type CustomRowUpdateData = {
  enabled: boolean;
  store_custom_menu_row_setting_id: number;
}[];
export type UpdateMenuProductParams = {
  attributes: Omit<MenuProductForProductsTable, 'custom_rows' | 'lineage'> & {
    custom_rows: CustomRowUpdateData;
    lineage: '' | 'cbd' | 'hybrid' | 'indica' | 'sativa' | 'none' | null;
  };
  deleted_photos: Photo[];
  photos: Photo[];
  photos_order: { [id: number]: string };
};

const updateMenuProduct = (
  storeId: string | number,
  id: string | number,
  menuProduct: UpdateMenuProductParams
): Promise<null> =>
  janeApiV2.patch(`${STORES_URL}/${storeId}/products/${id}`, {
    ...menuProduct,
  });
export const useUpdateMenuProduct = (
  storeId: string | number,
  id: string | number
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: UpdateMenuProductParams) =>
      updateMenuProduct(storeId, id, params),
    onError: (error) => {
      console.error(error);
    },
    onSuccess: () => {
      invalidateAllProductQueries(queryClient, storeId);
    },
  });
};

const fetchGlobalProductDetails = async (menuProductId?: string) => {
  const url = `/business/specials/products/${menuProductId}`;
  const response = await janeApiV2.get<{ data: GlobalSpecialProduct }>(url);

  return response;
};

export const useFetchMultipleGlobalProductDetails = (
  ids: number[],
  enabled: boolean
) => {
  return useQueries({
    queries: !enabled
      ? []
      : ids.map((id) => {
          return {
            queryKey: ['globalProducts', id],
            queryFn: () => fetchGlobalProductDetails(id.toString()),
          };
        }),
  });
};
